/**
 * SETTINGS-01 — Settings modal (replaces the right-hand SettingsDrawer).
 *
 * A centered, opaque MUI `<Dialog>` laid out as two panes: a left vertical
 * category menu (`<Tabs orientation="vertical">`) + a right content pane
 * that scrolls independently. The seven categories (Christian's locked
 * grouping, SETTINGS-01 Q3 → option a):
 *
 *   Layout & Widgets · Viewer · Bounding Boxes · List & Sorting ·
 *   Image States · Shortcuts · General
 *
 * Every setting that lived in the old drawer is preserved (same testids,
 * same persistence via Story 034's debounced `useUpdateLysePreferencesMutation`)
 * — this is a container + IA change only. Each tab carries a "Reset this
 * tab to defaults" action (Q5). The active tab is remembered in
 * `workspaceUiStore` so re-opening returns to it (AC4). Width is responsive
 * (~1400 px on desktop, scaling on larger screens — Q2; the analysis app is
 * desktop-only).
 *
 * The Bounding Boxes tab currently holds the legacy colour toggles; it is
 * replaced wholesale by the real appearance controls in SETTINGS-02.
 */
import { useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  useWorkspaceUiStore,
  SETTINGS_TABS,
  type SettingsTab,
} from "../state/workspaceUiStore.js";
import {
  useLysePreferencesQuery,
  useUpdateLysePreferencesMutation,
  BBOX_PREFS_DEFAULTS,
  type SortPreference,
} from "../../preferences/index.js";
import { useViewerSettings } from "../viewer/useViewerSettings.js";
// ANA-R3-TILE-03 — Tiles display settings (max-zoom cap + tile size).
import {
  useTileSettings,
  TILE_MIN_ZOOM,
  TILE_MAX_ZOOM,
} from "../viewer/useTileSettings.js";
import { TILE_PREFS_DEFAULTS, type TileSize } from "../../preferences/index.js";
import { BoundingBoxSettingsTab } from "./BoundingBoxSettingsTab.js";
import { StorageSettings } from "./StorageSettings.js";
import {
  useImageStateFilterStore,
  type ImageState,
} from "../state/imageStateFilterStore.js";
import { useCataloguesStore } from "../../catalogues/cataloguesStore.js";
import { MultiWindowLayoutSection } from "../multiwindow/index.js";
import {
  useAdvancedFilterWidgetStore,
  ADVANCED_FILTER_WIDGETS,
  type AdvancedFilterWidgetKey,
} from "../state/advancedFilterWidgetStore.js";
// Story 056 — the on-demand Histogram widget (persisted open/closed state).
import { useHistogramWidgetStore } from "../histogram/histogramWidgetStore.js";
// TRIAGE-01 (audit 3.2) — configurable triage keymap + auto-advance toggle.
import { TriageShortcutsSettings } from "../triage/TriageShortcutsSettings.js";
import { useTriagePreferencesStore } from "../triage/triagePreferencesStore.js";
import { RotateCcwIcon, XIcon } from "@aeriosiq/ui/icons";
// DI-3-S61 — Settings → History (the page's data set, read-only).
import { HistoryPanel, type HistoryContext } from "./history/HistoryPanel.js";

const WIDGET_KEYS = [
  { key: "sketch", label: "Component" },
  { key: "list", label: "List" },
  { key: "viewer", label: "Viewer" },
  { key: "tiles", label: "Tiles" },
  { key: "filmstrip", label: "Filmstrip" },
  { key: "split", label: "Split view" },
] as const;

// SB6-ANA-FILT-06 (D5) — the two advanced filter widgets. Toggled here
// (Layout & Widgets) AND from the L3 Kind sliders icons; both drive the
// shared `advancedFilterWidgetStore`, which surfaces them into the first
// empty grid region (never repositioning visible widgets).
const ADVANCED_FILTER_WIDGET_KEYS: readonly {
  key: AdvancedFilterWidgetKey;
  label: string;
}[] = [
  { key: "aiFilter", label: "AI Filter Module" },
  { key: "defectFilter", label: "Defect Filter Widget" },
];

const TAB_LABELS: Record<SettingsTab, string> = {
  layout: "Layout & Widgets",
  viewer: "Viewer",
  tiles: "Tiles",
  boundingBoxes: "Bounding Boxes",
  list: "List & Sorting",
  imageStates: "Image States",
  filterBehavior: "Filter Behavior",
  // SB6-ANA-CACHE-03 — image-cache storage management.
  storage: "Storage",
  shortcuts: "Shortcuts",
  general: "General",
  // DI-3-S61 — read-only: what happened to this data set.
  history: "History",
};

// SB6-ANA-FILT-05 (D3) — count-unit options for the Global Data Point
// Filter. The widget passes the chosen unit to the facets query.
const COUNT_UNIT_OPTIONS: readonly {
  value: "dataPoints" | "annotations" | "defects";
  label: string;
}[] = [
  { value: "dataPoints", label: "Data Points" },
  { value: "annotations", label: "Annotations" },
  { value: "defects", label: "Defects" },
];

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      component="h2"
      variant="subtitle2"
      sx={{
        textTransform: "uppercase",
        letterSpacing: 0.5,
        color: "text.secondary",
        fontWeight: 600,
        mt: 2,
        mb: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

function tabA11y(tab: SettingsTab) {
  return {
    id: `settings-tab-${tab}`,
    "aria-controls": `settings-tabpanel-${tab}`,
  };
}

export function SettingsModal({
  history,
}: {
  /** DI-3-S61 — the analysis page's data set, for the History section. */
  history?: HistoryContext | null;
} = {}): React.ReactElement {
  const open = useWorkspaceUiStore((s) => s.settingsOpen);
  const close = useWorkspaceUiStore((s) => s.closeSettings);
  const activeTab = useWorkspaceUiStore((s) => s.settingsTab);
  const setTab = useWorkspaceUiStore((s) => s.setSettingsTab);
  // SB6-ANA-HEADER-02 — the canvas reset-layout icon moved into this tab.
  const resetLayout = useWorkspaceUiStore((s) => s.resetLayout);

  const { prefs } = useLysePreferencesQuery();
  const { update } = useUpdateLysePreferencesMutation();
  const setImageStates = useImageStateFilterStore((s) => s.setStates);
  const imageStates = useImageStateFilterStore((s) => s.states);
  const favouriteCount = useCataloguesStore((s) => {
    const map = s.favouriteCatalogueEntries as
      | typeof s.favouriteCatalogueEntries
      | undefined;
    if (!map) return 0;
    let total = 0;
    for (const ids of Object.values(map)) total += ids.length;
    return total;
  });

  const {
    viewer: viewerPrefs,
    setMaxZoomPercent,
    setShowDebugOverlay,
  } = useViewerSettings();

  // ANA-R3-TILE-03 — Tiles display settings (max-zoom cap + tile size).
  // Deliberately separate from the Viewer max-zoom above so the two are
  // never confused.
  const {
    tiles: tilePrefs,
    setMaxZoomPercent: setTilesMaxZoomPercent,
    setTileSize,
  } = useTileSettings();

  // SB6-ANA-FILT-06 (D5) — advanced filter widget visibility (shared with
  // the L3 Kind sliders icons).
  const advVisible = useAdvancedFilterWidgetStore((s) => s.visible);
  const setAdvVisible = useAdvancedFilterWidgetStore((s) => s.setVisible);

  // Story 056 — Histogram widget visibility (persisted in the widget's own
  // localStorage envelope; the workspace grid reconciles against it).
  const histogramOpen = useHistogramWidgetStore((s) => s.open);
  const setHistogramOpen = useHistogramWidgetStore((s) => s.setOpen);

  // Helpers ----------------------------------------------------------------
  const toggleWidget = useCallback(
    (key: string, visible: boolean) => {
      update({ widgetVisibility: { [key]: visible } });
    },
    [update],
  );
  const setSort = useCallback(
    (next: SortPreference) => {
      update({ sortPreference: next });
    },
    [update],
  );
  const toggleInactive = useCallback(
    (showInactive: boolean) => {
      const next: ImageState[] = showInactive
        ? ["active", "inactive"]
        : ["active"];
      setImageStates(next);
      update({ imageStateFilter: next });
    },
    [setImageStates, update],
  );

  // SETTINGS-01 Q5 — per-tab reset to factory defaults. `null` = the tab
  // has nothing resettable (static reference / placeholder), so the
  // "Reset this tab" action is hidden for it.
  const resetTab = useCallback(
    (tab: SettingsTab) => {
      switch (tab) {
        case "layout":
          // Reset only widget visibility — NOT the per-DA `layout` map
          // (that holds the user's drag-arranged grid; clearing it here
          // would be a surprising data loss).
          update({ widgetVisibility: {} });
          // SB6-ANA-FILT-06 (D5) — the advanced filter widgets default to
          // hidden; reset turns them off.
          for (const key of ADVANCED_FILTER_WIDGETS) {
            setAdvVisible(key, false);
          }
          // Story 056 — the histogram defaults to closed; reset closes it.
          setHistogramOpen(false);
          break;
        case "viewer":
          update({ viewer: { maxZoomPercent: 500, showDebugOverlay: false } });
          break;
        case "tiles":
          // ANA-R3-TILE-03 — restore both Tiles defaults (max-zoom 1000 %,
          // size small).
          update({ tiles: TILE_PREFS_DEFAULTS });
          break;
        case "boundingBoxes":
          update({ boundingBox: BBOX_PREFS_DEFAULTS });
          break;
        case "list":
          update({ sortPreference: "catalogue" });
          break;
        case "imageStates":
          setImageStates(["active"]);
          update({ imageStateFilter: ["active"] });
          break;
        case "filterBehavior":
          update({ globalDataPointFilterCountUnit: "dataPoints" });
          break;
        case "shortcuts":
          // TRIAGE-01 — restore the default keymap + auto-advance ON.
          useTriagePreferencesStore.getState().resetToDefaults();
          break;
        default:
          break;
      }
    },
    [update, setImageStates, setAdvVisible, setHistogramOpen],
  );
  const TAB_HAS_RESET: Record<SettingsTab, boolean> = {
    layout: true,
    viewer: true,
    tiles: true,
    boundingBoxes: true,
    list: true,
    imageStates: true,
    filterBehavior: true,
    // SB6-ANA-CACHE-03 — Storage has its own clear/clear-all + budget reset
    // semantics inline; no factory "reset this tab" action.
    storage: false,
    // TRIAGE-01 — resets the keymap + auto-advance to factory defaults.
    shortcuts: true,
    general: false,
    // DI-3-S61 — read-only, nothing to reset.
    history: false,
  };

  const sort = prefs.sortPreference;
  const widgetVisibility = prefs.widgetVisibility;
  const showInactive = imageStates.includes("inactive");
  // SB6-ANA-FILT-05 (D3) — count-unit setting.
  const countUnit = prefs.globalDataPointFilterCountUnit;
  const setCountUnit = useCallback(
    (next: "dataPoints" | "annotations" | "defects") => {
      update({ globalDataPointFilterCountUnit: next });
    },
    [update],
  );

  // Tab content -------------------------------------------------------------
  function renderTab(tab: SettingsTab): React.ReactNode {
    switch (tab) {
      case "layout":
        return (
          <>
            <SectionHeading>Widget visibility</SectionHeading>
            <FormGroup>
              {WIDGET_KEYS.map((w) => {
                const visible = widgetVisibility[w.key] !== false;
                return (
                  <FormControlLabel
                    key={w.key}
                    control={
                      <Checkbox
                        size="small"
                        checked={visible}
                        onChange={(e) => {
                          toggleWidget(w.key, e.target.checked);
                        }}
                        inputProps={{ "aria-label": `Show ${w.label}` }}
                      />
                    }
                    label={w.label}
                  />
                );
              })}
              {/* Story 056 — the Histogram widget's rail toggle. Unlike the
                  preference-backed rows above it drives the widget's OWN
                  persisted store (the single visibility mechanism the
                  workspace grid reconciles against). */}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={histogramOpen}
                    data-testid="settings-widget-histogram"
                    onChange={(e) => {
                      setHistogramOpen(e.target.checked);
                    }}
                    inputProps={{ "aria-label": "Show Histogram" }}
                  />
                }
                label="Histogram"
              />
            </FormGroup>
            <SectionHeading>Advanced filters</SectionHeading>
            <FormGroup>
              {ADVANCED_FILTER_WIDGET_KEYS.map((w) => (
                <FormControlLabel
                  key={w.key}
                  control={
                    <Checkbox
                      size="small"
                      checked={advVisible[w.key]}
                      data-testid={`settings-widget-${w.key}`}
                      onChange={(e) => {
                        setAdvVisible(w.key, e.target.checked);
                      }}
                      inputProps={{ "aria-label": `Show ${w.label}` }}
                    />
                  }
                  label={w.label}
                />
              ))}
            </FormGroup>
            <SectionHeading>Multi-window layout</SectionHeading>
            <MultiWindowLayoutSection />
            {/* SB6-ANA-HEADER-02 — "Reset analysis widgets to default"
                relocated here from the canvas icon. Restores the
                first-open layout (the same imperative handleResetLayout the
                old icon called, published into the workspace UI store by
                AnalysisWorkspace). Hidden when no workspace is mounted. */}
            <SectionHeading>Reset layout</SectionHeading>
            <Stack spacing={1} sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Restore every analysis widget to its default position, size and
                visibility (the first-open arrangement).
              </Typography>
              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={<RotateCcwIcon fontSize="small" />}
                  disabled={resetLayout === null}
                  onClick={() => {
                    resetLayout?.();
                  }}
                  data-testid="settings-reset-layout"
                >
                  Reset analysis widgets to default
                </Button>
              </Box>
            </Stack>
          </>
        );
      case "viewer":
        return (
          <>
            <SectionHeading>Viewer</SectionHeading>
            <Stack spacing={1.25} sx={{ mb: 1 }}>
              <Box>
                <FormLabel
                  htmlFor="viewer-max-zoom-slider"
                  sx={{ fontSize: 13, display: "block", mb: 0.5 }}
                >
                  Max zoom:{" "}
                  <Box
                    component="span"
                    data-testid="viewer-max-zoom-value"
                    sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}
                  >
                    {viewerPrefs.maxZoomPercent} %
                  </Box>
                </FormLabel>
                <Slider
                  id="viewer-max-zoom-slider"
                  data-testid="viewer-max-zoom-slider"
                  min={100}
                  max={1000}
                  step={50}
                  value={viewerPrefs.maxZoomPercent}
                  onChange={(_e, v) => {
                    if (typeof v === "number") setMaxZoomPercent(v);
                  }}
                  aria-label="Maximum zoom level"
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${String(v)} %`}
                  marks={[
                    { value: 100, label: "100" },
                    { value: 500, label: "500" },
                    { value: 1000, label: "1000" },
                  ]}
                  sx={{ maxWidth: 460 }}
                />
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={viewerPrefs.showDebugOverlay}
                    onChange={(e) => {
                      setShowDebugOverlay(e.target.checked);
                    }}
                    inputProps={{ "aria-label": "Show debug overlay" }}
                    data-testid="viewer-debug-overlay-toggle"
                  />
                }
                label="Show debug overlay (pixel coords)"
              />
            </Stack>
          </>
        );
      case "tiles":
        // ANA-R3-TILE-03 — two independent Tiles settings. The max-zoom
        // slider (100–2000 %) caps the per-tile crop magnification; the
        // 3-option control picks the grid cell size. Both persist via
        // LysePreferences.tiles (useTileSettings). Kept visually distinct
        // from the Viewer "Max zoom" above so the two are not confused.
        return (
          <>
            <SectionHeading>Tiles</SectionHeading>
            <Stack spacing={2} sx={{ mb: 1 }}>
              <Box>
                <FormLabel
                  htmlFor="tiles-max-zoom-slider"
                  sx={{ fontSize: 13, display: "block", mb: 0.5 }}
                >
                  Max zoom (tiles):{" "}
                  <Box
                    component="span"
                    data-testid="tiles-max-zoom-value"
                    sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}
                  >
                    {tilePrefs.maxZoomPercent} %
                  </Box>
                </FormLabel>
                <Slider
                  id="tiles-max-zoom-slider"
                  data-testid="tiles-max-zoom-slider"
                  min={TILE_MIN_ZOOM}
                  max={TILE_MAX_ZOOM}
                  step={50}
                  value={tilePrefs.maxZoomPercent}
                  onChange={(_e, v) => {
                    if (typeof v === "number") setTilesMaxZoomPercent(v);
                  }}
                  aria-label="Maximum tile zoom level"
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${String(v)} %`}
                  marks={[
                    { value: 100, label: "100" },
                    { value: 1000, label: "1000" },
                    { value: 2000, label: "2000" },
                  ]}
                  sx={{ maxWidth: 460 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Caps how far a tile crop may magnify a defect (% of native
                  resolution). Tiny defects fill the tile up to this cap instead
                  of hyper-zooming; larger defects frame with context.
                </Typography>
              </Box>
              <Box>
                <FormLabel
                  id="tiles-size-label"
                  sx={{ fontSize: 13, display: "block", mb: 0.5 }}
                >
                  Tile size
                </FormLabel>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={tilePrefs.tileSize}
                  onChange={(_e, v: TileSize | null) => {
                    if (v) setTileSize(v);
                  }}
                  aria-labelledby="tiles-size-label"
                >
                  <ToggleButton value="small" data-testid="tiles-size-small">
                    Small
                  </ToggleButton>
                  <ToggleButton value="medium" data-testid="tiles-size-medium">
                    Medium
                  </ToggleButton>
                  <ToggleButton value="large" data-testid="tiles-size-large">
                    Large
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Stack>
          </>
        );
      case "boundingBoxes":
        // SETTINGS-02 — full per-kind appearance controls + live preview.
        return <BoundingBoxSettingsTab />;
      case "list":
        return (
          <>
            <SectionHeading>Default sort</SectionHeading>
            <FormControl>
              <FormLabel id="settings-sort-label" sx={{ fontSize: 13 }}>
                Defects list sort
              </FormLabel>
              <RadioGroup
                aria-labelledby="settings-sort-label"
                value={sort}
                onChange={(_e, v) => {
                  setSort(v as SortPreference);
                }}
              >
                <FormControlLabel
                  value="catalogue"
                  control={<Radio size="small" />}
                  label="By catalogue order"
                />
                <FormControlLabel
                  value="severity"
                  control={<Radio size="small" />}
                  label="By severity"
                />
                <FormControlLabel
                  value="image"
                  control={<Radio size="small" />}
                  label="By image"
                />
              </RadioGroup>
            </FormControl>
            <SectionHeading>Favourites</SectionHeading>
            <Typography variant="body2" color="text.secondary">
              {favouriteCount === 0
                ? "No catalogue entries starred yet — open the Catalogue popover (Ctrl + K) to star one."
                : `${String(favouriteCount)} catalogue entries starred. Manage them in the Catalogue popover (Ctrl + K).`}
            </Typography>
          </>
        );
      case "imageStates":
        return (
          <>
            <SectionHeading>Image states</SectionHeading>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox size="small" checked disabled />}
                label="Show active images"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={showInactive}
                    onChange={(e) => {
                      toggleInactive(e.target.checked);
                    }}
                    inputProps={{ "aria-label": "Show inactive images" }}
                  />
                }
                label="Show inactive images"
              />
            </FormGroup>
          </>
        );
      case "filterBehavior":
        return (
          <>
            <SectionHeading>Global Data Point Filter</SectionHeading>
            <FormControl sx={{ mt: 1, minWidth: 260 }} size="small">
              <FormLabel
                id="filter-count-unit-label"
                sx={{ fontSize: 13, mb: 0.5 }}
              >
                Global Data Point Filter Count Unit
              </FormLabel>
              <Select
                labelId="filter-count-unit-label"
                value={countUnit}
                data-testid="filter-count-unit-select"
                onChange={(e) => {
                  setCountUnit(
                    e.target.value as "dataPoints" | "annotations" | "defects",
                  );
                }}
                inputProps={{
                  "aria-label": "Global Data Point Filter Count Unit",
                }}
              >
                {COUNT_UNIT_OPTIONS.map((o) => (
                  <MenuItem
                    key={o.value}
                    value={o.value}
                    data-testid={`filter-count-unit-${o.value}`}
                  >
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Determines what the bracket count next to each filter option
              counts. Data Points is the default, so the L3 AI option reads as
              the number of data points carrying AI findings.
            </Typography>
          </>
        );
      case "storage":
        // SB6-ANA-CACHE-03 — image-cache storage management.
        return <StorageSettings />;
      case "shortcuts":
        // TRIAGE-01 — configurable triage keymap (rebind via capture-
        // next-keypress) + auto-advance toggle + static reference list.
        return <TriageShortcutsSettings />;
      case "general":
        return (
          <>
            <SectionHeading>Repair info</SectionHeading>
            <Alert severity="info" sx={{ mb: 1 }} icon={false}>
              Repair info workflow — coming in a future release.
            </Alert>
          </>
        );
      case "history":
        // DI-3-S61 — head, coverage with the acknowledgements, timeline.
        return <HistoryPanel context={history} />;
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth={false}
      aria-labelledby="settings-modal-title"
      // Dim the app behind a touch more than MUI's default 0.5 so the
      // glowing modal reads as clearly lifted off the dark canvas.
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.6)" } } }}
      PaperProps={{
        sx: (theme) => ({
          // Desktop-only app (Q2): a generous default that scales on
          // larger screens but never overflows narrow ones.
          width: "min(1400px, 94vw)",
          height: "min(880px, 90vh)",
          maxWidth: "none",
          // Opaque surface — modals never use the glass treatment
          // (03_COLOR.md §3.5).
          bgcolor: "background.paper",
          // Christian 2026-05-26 — separate the modal from the dark app
          // behind with a Signal-Blue context glow: a crisp accent ring,
          // a soft blue halo, and a deep neutral drop shadow. Theme-aware
          // (accent = #0071E3 light / #2997FF dark).
          border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
          boxShadow: [
            `0 0 0 1px ${alpha(theme.palette.primary.main, 0.45)}`,
            `0 0 48px ${alpha(theme.palette.primary.main, 0.2)}`,
            "0 32px 80px rgba(0,0,0,0.55)",
          ].join(", "),
        }),
      }}
    >
      <Stack direction="row" sx={{ height: "100%", minHeight: 0 }}>
        {/* Left category menu --------------------------------------------- */}
        <Box
          sx={{
            width: 220,
            flexShrink: 0,
            borderRight: 1,
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            // Raised surface (not background.default) — in dark mode the
            // canvas colour matched the app behind and read as a black
            // hole. Using background.paper makes the rail part of the
            // lifted modal; a hair of accent tint separates it from the
            // content pane.
            bgcolor: "background.paper",
            backgroundImage: (theme) =>
              `linear-gradient(${alpha(theme.palette.primary.main, 0.04)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          }}
        >
          <Typography
            id="settings-modal-title"
            variant="h6"
            component="h1"
            sx={{ fontWeight: 600, px: 2, py: 1.75 }}
          >
            Settings
          </Typography>
          <Divider />
          <Tabs
            orientation="vertical"
            value={activeTab}
            onChange={(_e, v: SettingsTab) => {
              setTab(v);
            }}
            aria-label="Settings categories"
            sx={{
              flexGrow: 1,
              "& .MuiTab-root": {
                alignItems: "flex-start",
                textAlign: "left",
                textTransform: "none",
                minHeight: 44,
                fontSize: 14,
              },
            }}
          >
            {SETTINGS_TABS.map((t) => (
              <Tab key={t} value={t} label={TAB_LABELS[t]} {...tabA11y(t)} />
            ))}
          </Tabs>
        </Box>

        {/* Right content pane --------------------------------------------- */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: "divider" }}
          >
            <Typography
              variant="subtitle1"
              component="h2"
              sx={{ fontWeight: 600 }}
            >
              {TAB_LABELS[activeTab]}
            </Typography>
            <Tooltip title="Close">
              <IconButton
                size="small"
                onClick={close}
                aria-label="Close settings"
              >
                <XIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Box
            role="tabpanel"
            id={`settings-tabpanel-${activeTab}`}
            aria-labelledby={`settings-tab-${activeTab}`}
            sx={{ flexGrow: 1, overflowY: "auto", px: 3, pb: 2 }}
          >
            {renderTab(activeTab)}
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: "divider" }}
          >
            {TAB_HAS_RESET[activeTab] ? (
              <Button
                size="small"
                color="inherit"
                startIcon={<RotateCcwIcon fontSize="small" />}
                onClick={() => {
                  resetTab(activeTab);
                }}
              >
                Reset this tab
              </Button>
            ) : (
              <span />
            )}
            <Button size="small" variant="contained" onClick={close}>
              Done
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Dialog>
  );
}
